<script setup lang="ts">
import { storeToRefs } from "pinia";
import type { Ref } from "vue";
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import { OperationModelType } from "~/core/models/OperationModelType.enum";
import { useFetchDtwinOperationModel } from "~/modules/dtwin/composables/useFetchDtwinOperationModel";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { DtwinLifeCycleState } from "~/modules/dtwin/models/DtwinLifeCycleState.enum";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";
import type { DynamicUIDetail } from "~/modules/dtwin/models/DynamicUIDetail.interface";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import ILxcPlus from "~icons/lxc/plus";

const props = defineProps<{
  organizationCode: number;
  dtwin?: Dtwin;
  dtwinModel?: DtwinModel;
  uiConfigFirmware?: Record<string, Record<string, DynamicUIDetail[]>>;
}>();

const { t } = useI18n();

const { can } = useAcl();

const { currentOrganization } = storeToRefs(useOrganizationStore());

/**
 * Dtwin operation model
 */
const {
  dtwinOperationModel,
  isOperationModelEnabled,
  isLoading,
  error,
  fetchDtwinOperationModel,
} = useFetchDtwinOperationModel({
  organizationCode: currentOrganization.value?.code,
  operationType: OperationModelType.FIRM_UPDATE,
});
/**
 * End Dtwin operation model
 */

const updateFirmwareModalShow: Ref<boolean> = ref(false);
const reloadOperationList: Ref<boolean> = ref(false);

const canUpdateOperations: ComputedRef<boolean> = computed(() =>
  can([ACLRoles.CAN_MANAGE_FIRMWARE_OPERATION, ACLRoles.CAN_READ_FIRMWARE]),
);

const showUpdateFirmwareModal = () => {
  updateFirmwareModalShow.value = true;
};

const doReloadOperationList = () => {
  reloadOperationList.value = !reloadOperationList.value;
};

onMounted(async () => {
  await fetchDtwinOperationModel(props.dtwinModel);
});
</script>

<template>
  <container-component :px="0" :py="0" :is-loading="isLoading" :error="error">
    <dtwin-form-firmware-information
      :dtwin="dtwin"
      :ui-config-firmware="uiConfigFirmware"
    />

    <!-- Firmware update operations are available only if a model operation exists for the device type -->
    <block-container
      v-if="
        canUpdateOperations &&
        dtwin &&
        dtwinModel &&
        dtwinOperationModel &&
        isOperationModelEnabled
      "
      title-top
      with-margin-bottom
    >
      <lxc-button
        type="primary"
        html-type="button"
        :icon="ILxcPlus"
        :title="t('dtwins.form.firmware.updateFirmware')"
        :disabled="dtwin.lifeCycleState === DtwinLifeCycleState.DISABLED"
        @click.prevent="showUpdateFirmwareModal"
      >
        {{ t("dtwins.form.firmware.updateFirmware") }}
      </lxc-button>
    </block-container>

    <block-container
      v-if="
        can(ACLRoles.CAN_MANAGE_FIRMWARE_OPERATION) &&
        dtwin &&
        dtwinModel &&
        dtwinOperationModel &&
        isOperationModelEnabled
      "
      :title="t('section.operations.title')"
      title-top
    >
      <dtwin-form-firmware-operation-list
        :organization-code="organizationCode"
        :dtwin="dtwin"
        :dtwin-model="dtwinModel"
        :dtwin-operation-model="dtwinOperationModel"
        :reload="reloadOperationList"
      />
    </block-container>

    <dtwin-form-firmware-update-modal
      v-if="canUpdateOperations && dtwin && dtwinModel && dtwinOperationModel"
      v-model:show="updateFirmwareModalShow"
      :organization-code="organizationCode"
      :dtwin="dtwin"
      :dtwin-model="dtwinModel"
      :dtwin-operation-model="dtwinOperationModel"
      @change="doReloadOperationList"
    />
  </container-component>
</template>
